import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { ListParams } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import type { PaginatedResult } from '../../types/api';

interface ColumnDef<T> {
    key: string;
    header: string;
    render: (row: T) => ReactNode;
}

interface ResourceBrowsePageProps<T extends { id: string }> {
    title: string;
    description?: string;
    queryKey: string;
    fetchList: (params: ListParams) => Promise<PaginatedResult<T>>;
    fetchOne?: (id: string) => Promise<T>;
    columns: ColumnDef<T>[];
    searchMode?: 'list' | 'search';
    searchParamKey?: string;
    filters?: ReactNode;
    /** Merged into every list/search request (e.g. status or type filters). */
    extraListParams?: Record<string, string | number | undefined>;
    /** When set, row click only updates `?id=` (detail handled by parent). */
    linkDetailInUrl?: boolean;
}

export function ResourceBrowsePage<T extends { id: string }>({
    title,
    description,
    queryKey,
    fetchList,
    columns,
    searchMode = 'list',
    searchParamKey = 'q',
    filters,
    extraListParams,
    linkDetailInUrl = true,
}: ResourceBrowsePageProps<T>) {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const listParams: ListParams = {
        page,
        ...extraListParams,
        ...(searchMode === 'search' && search ? { [searchParamKey]: search } : {}),
    };

    const { data, isLoading } = useListQuery(queryKey, fetchList, listParams);

    useEffect(() => {
        const id = searchParams.get('id');
        if (id) {
            setPage(1);
        }
    }, [searchParams]);

    useEffect(() => {
        setPage(1);
    }, [extraListParams]);

    return (
        <Stack gap="lg">
            <PageHeader title={title} description={description} />
            {filters ? <DataPanel>{filters}</DataPanel> : null}
            {searchMode === 'search' ? (
                <DataPanel>
                    <TextInput
                        label={t('common.search.label')}
                        placeholder={t('common.search.placeholder')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.currentTarget.value);
                            setPage(1);
                        }}
                    />
                </DataPanel>
            ) : null}
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={columns}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={
                    linkDetailInUrl
                        ? (row) => setSearchParams({ id: row.id })
                        : undefined
                }
            />
        </Stack>
    );
}
